import { Box, Container, VStack, useToast, Text } from "@chakra-ui/react";
import { ReactComponent as Logo } from "assets/logos/s-logo.svg";
import PinInput from "components/Input/PinInput";
import Button from "components/Button/Button";
import { useState, useRef, useEffect } from "react";
import Recaptcha from "react-google-recaptcha";

import {
  useVerifyEmailMutation,
  useResendEmailPinMutation,
} from "services/arbitraryApi";

import { useHistory } from "react-router-dom";

import useCountDownTimer from "hooks/useCountDownTimer";
import { addMinutes } from "date-fns";

import { hydrateToken } from "store/slices/auth.slice";
import { useDispatch } from "react-redux";

import constant from "config/constant";

const VerifyEmail = () => {
  const pinMax = 6;
  const toast = useToast();
  const history = useHistory();
  const dispatch = useDispatch();

  const recaptchaInstanceRef = useRef();
  const [pin, setPin] = useState("");

  const { Timer} = useCountDownTimer(
    addMinutes(Date.now(), 10)
  );

  const [hasCompleteValues, setHasCompleteValues] = useState(false);

  const [verifyEmailPin, { isLoading, isSuccess, isError, error, data }] =
    useVerifyEmailMutation();

  const [
    resendPin,
    {
      isLoading: isResendingPin,
      isSuccess: isResendPinSuccess,
      isError: isResendPinError,
      error: resendPinError,
      data: resendPinData,
    },
  ] = useResendEmailPinMutation();

  const getError = (error) => {
    function isObj(obj) {
      var type = typeof obj;
      return type === "function" || (type === "object" && !!obj);
    }
    const value = isObj(error) ? Object.values(error)[0] : error;
    // console.log("ERROR VALUE", value);
    return value ?? "An unexpected error occurred";
  };

  const handlePin = (pin) => {
    const isValid = String(pin).length === pinMax;
    if (!isValid) setHasCompleteValues(false);
    setPin(pin);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (navigator.onLine === true) {
      const captcha = await recaptchaInstanceRef.current?.executeAsync();
      const reqData = { code: pin, captcha };
      console.log("PIN", pin, reqData);
      verifyEmailPin(reqData);
    } else {
      toast({
        position: "bottom-right",
        title: "Network Error!!",
        description: "Please check your network connection",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  };

  const handleResendPin = async () => {
    if (navigator.onLine === true) {
      //   recaptchaInstanceRef.current?.reset();
      const captcha = await recaptchaInstanceRef.current?.executeAsync();
      console.log("PIN", pin, captcha);
      resendPin(captcha);
    } else {
      toast({
        position: "bottom-right",
        title: "Network Error!!",
        description: "Please check your network connection",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    if (isSuccess && data) {
      console.log("EMAIL VERIFICATION RESULT", data);
      recaptchaInstanceRef.current?.reset();

      dispatch(hydrateToken({ token: data.token, isSignedIn: false }));
      toast({
        position: "bottom-right",
        title: "Success",
        description: "Email validation successful",
        status: "success",
        duration: 9000,
        isClosable: true,
      });

      if (!data.isVerified) history.replace(constant.PAGES.VERIFY_PHONE);
    }

    if (isError && error) {
      console.log("EMAIL VERIFICATION ERROR", error);
      toast({
        position: "bottom-right",
        title: "Oops!!",
        description: getError(error?.data?.error),
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  }, [isSuccess, data, isError, error, dispatch, history, toast]);

  useEffect(() => {
    if (isResendPinSuccess && resendPinData) {
      console.log("EMAIL VERIFICATION RESULT", resendPinData);
      recaptchaInstanceRef.current?.reset();

      //   dispatch(hydrateToken(data.token))
      toast({
        position: "bottom-right",
        title: "Success",
        description: "Code sent",
        status: "success",
        duration: 9000,
        isClosable: true,
      });

      //   history.replace("/dashboard/home");
    }

    if (isResendPinError && resendPinError) {
      console.log("EMAIL VERIFICATION ERROR", error);
      toast({
        position: "bottom-right",
        title: "Oops!!",
        description: getError(error?.data?.error),
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  }, [
    isResendPinSuccess,
    resendPinData,
    isResendPinError,
    resendPinError,
    error,
    toast,
  ]);

  return (
    <VStack py={["10px", "20px"]} minH="100vh" alignItems="center">
      <Container maxW="container.sm">
        <Box bg="#fcfcfc" p={["10px", "16px", "28px"]}>
          <VStack>
            <Box w={["100px", "initial"]} as={Logo} />
            {/* <Heading as="h3" fontSize={["24px", "32px"]}>
              Welcome
            </Heading> */}
            <Text
              textAlign="center"
              fontSize={["32px", "3xl"]}
              mt="24px !important"
            >
              Verify your email
            </Text>
            <Text
              textAlign="center"
              fontSize={["sm", "md"]}
              color="#4e4b66"
              //   mt="24px !important"
            >
              Enter the 6-digit code sent to your email
            </Text>
          </VStack>

          <Box as="form" mt="50px" onSubmit={handleSubmit}>
            <VStack>
              <PinInput
                isRequired
                limit={pinMax}
                size={["lg"]}
                onChange={handlePin}
                onComplete={() => setHasCompleteValues(true)}
              />

              <Recaptcha
                ref={recaptchaInstanceRef}
                sitekey={process.env.REACT_APP_RECAPTCHA}
                size={"invisible"}
              />

              <Button
                disabled={!hasCompleteValues || isLoading}
                isLoading={isLoading}
                minW="204px"
                minH="56px"
                fontWeight="normal"
                mt="30px !important"
                type="submit"
              >
                Submit
              </Button>

              <Timer mt="50px !important" />

              <Button
                // disabled={!timedout || isResendingPin}
                isLoading={isResendingPin}
                // minW="204px"
                // minH="56px"
                // disabled
                variant="ghost"
                fontWeight="normal"
                mt="10px !important"
                bg="transparent"
                color="#02a438"
                size="sm"
                w="fit-content"
                onClick={handleResendPin}
                // type="submit"
              >
                Resend Code
              </Button>
            </VStack>
          </Box>
        </Box>
      </Container>
    </VStack>
  );
};

export default VerifyEmail;
